import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, requestWithoutContent, withQuery } from "../http/httpClient";
import {
  readArray,
  readBoolean,
  readInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
} from "../http/jsonDecoding";

/**
 * Memories (`docs/components/memory.md`): a title, an optional note and the exact Thread
 * passages they rest on, kept by people or saved by agents. Each is its owner's alone
 * (J-01 (a)): the routes list and show only the caller's own. Routes:
 * `server/src/dmw/api/routes/threads/memories.py`. Built provisionally, awaiting acceptance.
 */

export type MemoryState = ApiSchema<"MemoryState">;
/** What a person does to a memory by hand. */
export type MemoryAction = ApiSchema<"MemoryAction">;

export const MEMORY_STATES = enumMembers<MemoryState>({ proposed: true, active: true, stale: true, retired: true, discarded: true });
const ORIGIN_KINDS = ["person", "agent"] as const;

export interface MemorySource {
  readonly entry: string;
  readonly threadTitle: string;
  /** The passage, verbatim, read from its entry. */
  readonly text: string;
}

export interface Memory {
  readonly memoryId: string;
  readonly ownerUserId: string;
  readonly title: string;
  readonly note: string | null;
  readonly reason: string | null;
  readonly origin: (typeof ORIGIN_KINDS)[number];
  readonly state: MemoryState;
  readonly lastUsedAt: string | null;
  readonly useCount: number;
  readonly sources: readonly MemorySource[];
  /** The caller may keep, retire, restore and delete it. */
  readonly mayChange: boolean;
  /** It waits for confirmation and the caller may give it. */
  readonly mayConfirm: boolean;
  /** Its own small Thread, a run of "Keep a memory" (ADR-0031 D5); `null` for one kept before. */
  readonly keptIn: string | null;
  /** The Thread, and its step if one, it was kept from. */
  readonly attachedTo: { readonly threadId: string; readonly stepKey: string | null } | null;
}

function decodeSource(value: unknown): MemorySource {
  const object = readObject(value, "memory source");
  return { entry: readString(object, "entry"), threadTitle: readString(object, "thread_title"), text: readString(object, "text") };
}

export function decodeMemory(value: unknown): Memory {
  const object = readObject(value, "memory");
  return {
    memoryId: readString(object, "memory_id"),
    ownerUserId: readString(object, "owner_user_id"),
    title: readString(object, "title"),
    note: readNullableString(object, "note"),
    reason: readNullableString(object, "reason"),
    origin: readOneOf(readObject(object["origin"], "memory origin"), "kind", ORIGIN_KINDS),
    state: readOneOf(object, "state", MEMORY_STATES),
    lastUsedAt: readNullableString(object, "last_used_at"),
    useCount: readInteger(object, "use_count"),
    sources: readArray(object["sources"], "memory sources", decodeSource),
    mayChange: readBoolean(object, "may_change"),
    mayConfirm: readBoolean(object, "may_confirm"),
    keptIn: readNullableString(object, "kept_in"),
    attachedTo: decodeAttachment(object["attached_to"]),
  };
}

function decodeAttachment(value: unknown): Memory["attachedTo"] {
  if (value === null || value === undefined) return null;
  const attachment = readObject(value, "memory attachment");
  return { threadId: readString(attachment, "thread_id"), stepKey: readNullableString(attachment, "step_key") };
}

const memoriesPath = (projectId: string, rest = ""): `/${string}` => `/projects/${encodeURIComponent(projectId)}/memories${rest}`;

/** Which Thread's memories: those kept from it (`attachedTo`), or the one it keeps (`keptIn`, its own small Thread). */
export interface MemoryThreadFilter {
  readonly attachedTo?: string;
  readonly keptIn?: string;
}

/** The catalogue: by state, or one Thread's memories. */
export function listMemories(projectId: string, state: MemoryState | null, signal?: AbortSignal, thread: MemoryThreadFilter = {}): Promise<readonly Memory[]> {
  return requestJson({
    method: "GET",
    path: withQuery(memoriesPath(projectId), { state, attached_to: thread.attachedTo ?? null, kept_in: thread.keptIn ?? null }),
    signal,
    decodeResponse: (body) => readArray(body, "memories", decodeMemory),
  });
}

/** One passage a person keeps: the exact text of an entry, and which appearance of it. */
export interface MemorySourceDraft {
  /** `<thread id>#<entry number>`. */
  readonly entry: string;
  readonly quote: string;
  readonly occurrence: number;
}

export interface MemoryDraft {
  readonly title: string;
  readonly note: string | null;
  readonly sources: readonly MemorySourceDraft[];
}

/** One memory, under the catalogue's read rule. */
export function readMemory(projectId: string, memoryId: string, signal?: AbortSignal): Promise<Memory> {
  return requestJson({ method: "GET", path: memoriesPath(projectId, `/${encodeURIComponent(memoryId)}`), signal, decodeResponse: decodeMemory });
}

/** New wording for a memory, from its Thread or a Workflow view (ADR-0031 D5); its state stays. */
export function editMemory(projectId: string, memoryId: string, wording: { readonly title: string; readonly note: string | null }): Promise<Memory> {
  return requestJson({ method: "PATCH", path: memoriesPath(projectId, `/${encodeURIComponent(memoryId)}`), body: jsonBody(wording), decodeResponse: decodeMemory });
}

export function changeMemory(projectId: string, memoryId: string, action: MemoryAction): Promise<Memory> {
  return requestJson({ method: "POST", path: memoriesPath(projectId, `/${encodeURIComponent(memoryId)}/${action}`), decodeResponse: decodeMemory });
}

export function deleteMemory(projectId: string, memoryId: string): Promise<void> {
  return requestWithoutContent({ method: "DELETE", path: memoriesPath(projectId, `/${encodeURIComponent(memoryId)}`) });
}
